import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Scatter,
} from 'recharts';
import type { Trace } from '@/features/traceability/type/Tool';
import { useMemo } from 'react';
import type { ChartConfig } from '../../type/dynamicChart';
import { COLORS } from '../../utils/palette';
import { getMetricFormatter, getMetricUnit } from '@/utils/format';
import { getMetricAxisFormatter, shortToolId } from '../../utils/format';
import { getMetricValue, isCategoricalXAxis } from '../../utils/helper';
import { aggregateTracesByReference } from '../../utils/trace';
import { SmartLegend } from './SmartLegend';
import type { LegendItem } from './SmartLegend';
import { ChartEmptyState } from './ChartEmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { getMetricLabelKey } from '../../utils/chartDefaults';

type TLBoxPlotChartProps = {
  config: ChartConfig;
  traces: Trace[];
};

// Check if X-axis is a categorical dimension
type BoxPlotStats = {
  category: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  mean: number;
  outliers: number[];
  count: number;
  traceCount?: number;
};

// Calculate boxplot statistics
function calculateBoxPlotStats(values: number[]): Omit<BoxPlotStats, 'category' | 'traceCount'> {
  if (values.length === 0) {
    return {
      min: 0,
      q1: 0,
      median: 0,
      q3: 0,
      max: 0,
      mean: 0,
      outliers: [],
      count: 0,
    };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;

  // Calculate quartiles
  const q1Index = Math.floor(n * 0.25);
  const medianIndex = Math.floor(n * 0.5);
  const q3Index = Math.floor(n * 0.75);

  const q1 = sorted[q1Index];
  const median = sorted[medianIndex];
  const q3 = sorted[q3Index];

  // Calculate IQR and outlier bounds
  const iqr = q3 - q1;
  const lowerBound = q1 - 1.5 * iqr;
  const upperBound = q3 + 1.5 * iqr;

  // Identify outliers
  const outliers = sorted.filter((v) => v < lowerBound || v > upperBound);

  // Calculate whiskers (min/max excluding outliers)
  const nonOutliers = sorted.filter((v) => v >= lowerBound && v <= upperBound);
  const min = nonOutliers.length > 0 ? nonOutliers[0] : sorted[0];
  const max = nonOutliers.length > 0 ? nonOutliers[nonOutliers.length - 1] : sorted[n - 1];

  // Calculate mean
  const mean = values.reduce((sum, v) => sum + v, 0) / n;

  return {
    min,
    q1,
    median,
    q3,
    max,
    mean,
    outliers,
    count: n,
  };
}

export function TLBoxPlotChart({ config, traces }: TLBoxPlotChartProps) {
  const { t } = useTranslation();

  // Check if X-axis is categorical dimension
  const isCategorical = isCategoricalXAxis(config.xAxis.metric);

  // For boxplot, we need to group traces by category and collect all values
  // This is different from other charts - we need the raw trace values, not aggregated
  const boxPlotData = useMemo(() => {
    // If aggregation is by reference, group by reference
    if (config.aggregatedBy === 'reference') {
      // Use aggregateTracesByReference to get reference groups
      const referenceGroups = aggregateTracesByReference(traces);
      return referenceGroups.map((refGroup) => {
        // For each reference, collect all values from traces with that reference
        const values = traces
          .filter(
            (trace) => (trace as Trace & { reference?: string }).reference === refGroup.reference
          )
          .map((trace) => getMetricValue(trace, config.yAxis.metric))
          .filter((v): v is number => v !== null);
        const stats = calculateBoxPlotStats(values);
        return {
          category: refGroup.reference,
          ...stats,
          traceCount: refGroup.traceCount,
          toolCount: refGroup.toolCount,
        };
      });
    }

    // Default: group by category/tool
    const groups = new Map<string, { values: number[]; traceCount: number }>();

    traces.forEach((trace) => {
      const value = getMetricValue(trace, config.yAxis.metric);
      if (value === null) return;

      // Determine category based on X-axis metric
      let category: string;
      if (isCategorical) {
        if (config.xAxis.metric === 'machine') {
          category = trace.machine?.name || t('analytics.dashboard.complexCharts.defaults.unknown');
        } else if (config.xAxis.metric === 'material') {
          category = trace.material?.name ?? t('analytics.dashboard.complexCharts.defaults.unknown');
        } else if (config.xAxis.metric === 'toolHolder') {
          category = trace.holderType || t('analytics.dashboard.complexCharts.defaults.unknown');
        } else if (config.xAxis.metric === 'lubrication') {
          category = trace.lubType || t('analytics.dashboard.complexCharts.defaults.unknown');
        } else {
          category = t('analytics.dashboard.complexCharts.defaults.unknown');
        }
      } else {
        // Group by tool
        category = shortToolId(trace.toolId);
      }

      if (!groups.has(category)) {
        groups.set(category, { values: [], traceCount: 0 });
      }
      groups.get(category)!.values.push(value);
      groups.get(category)!.traceCount++;
    });

    // Calculate box plot statistics for each group
    const boxPlots: BoxPlotStats[] = [];
    groups.forEach((data, category) => {
      const stats = calculateBoxPlotStats(data.values);
      boxPlots.push({
        category,
        ...stats,
        traceCount: data.traceCount,
      });
    });

    // Sort by median value (descending) or alphabetically
    if (config.options.sortOrder === 'desc') {
      return boxPlots.sort((a, b) => b.median - a.median);
    } else if (config.options.sortOrder === 'asc') {
      return boxPlots.sort((a, b) => a.median - b.median);
    } else if (config.options.sortOrder === 'alpha') {
      return boxPlots.sort((a, b) => a.category.localeCompare(b.category));
    }

    return boxPlots;
  }, [
    traces,
    config.yAxis.metric,
    config.xAxis.metric,
    isCategorical,
    config.options.sortOrder,
    config.aggregatedBy,
    t,
  ]);

  // Transform data for recharts
  // We'll use bars to represent the box and whiskers
  const chartData = useMemo(() => {
    return boxPlotData.map((box, index) => {
      // Calculate bar positions for box plot visualization
      const whiskerLower = box.min;
      const boxBottom = box.q1;
      const medianLine = box.median;
      const boxTop = box.q3;
      const whiskerUpper = box.max;

      return {
        category: box.category,
        index,
        // For stacked bars to create box plot
        whiskerLowerHeight: boxBottom - whiskerLower,
        boxLowerHeight: medianLine - boxBottom,
        boxUpperHeight: boxTop - medianLine,
        whiskerUpperHeight: whiskerUpper - boxTop,
        // Base values
        whiskerLowerBase: whiskerLower,
        boxLowerBase: boxBottom,
        boxUpperBase: medianLine,
        whiskerUpperBase: boxTop,
        // Stats for tooltip
        min: box.min,
        q1: box.q1,
        median: box.median,
        q3: box.q3,
        max: box.max,
        mean: box.mean,
        outliers: box.outliers,
        count: box.count,
        traceCount: box.traceCount,
        // Median point for visualization
        medianPoint: medianLine,
      };
    });
  }, [boxPlotData]);

  // Flatten outliers for scatter plot
  const outlierData = useMemo(() => {
    const outliers: Array<{ category: string; value: number; index: number }> = [];
    boxPlotData.forEach((box, index) => {
      box.outliers.forEach((value) => {
        outliers.push({ category: box.category, value, index });
      });
    });
    return outliers;
  }, [boxPlotData]);

  // Prepare legend items for SmartLegend
  const legendItems: LegendItem[] = useMemo(() => {
    return boxPlotData.map((box, index) => ({
      value: box.category,
      label: box.category,
      color: COLORS[index % COLORS.length],
    }));
  }, [boxPlotData]);

  const valueFormatter = getMetricFormatter(config.yAxis.metric);
  const yAxisFormatter = getMetricAxisFormatter(config.yAxis.metric);
  const unit = getMetricUnit(config.yAxis.metric);

  if (chartData.length === 0) {
    return <ChartEmptyState variant="no-data" />;
  }

  const showOutliers = config.options.showOutliers !== false;

  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={450}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 60, left: 52 }}>
          {config.options.showGridLines !== false && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis
            dataKey="category"
            angle={-45}
            textAnchor="end"
            height={52}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value: isCategorical ? t(getMetricLabelKey(config.xAxis.metric)) : t('analytics.dashboard.complexCharts.configuration.metrics.toolId'),
              position: 'insideBottom',
              offset: -40,
              fill: 'var(--muted-foreground)',
            }}
          />
          <YAxis
            tickFormatter={yAxisFormatter}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value: `${t(getMetricLabelKey(config.yAxis.metric))} ${unit ? `(${unit})` : ''}`,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              fill: 'var(--muted-foreground)',
            }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || payload.length === 0) return null;
              const data = payload[0].payload;

              return (
                <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                  <p className="font-medium mb-1">
                    {config.aggregatedBy === 'reference'
                      ? t('analytics.dashboard.complexCharts.tooltip.reference')
                      : isCategorical
                        ? t(getMetricLabelKey(config.xAxis.metric))
                        : t('analytics.dashboard.complexCharts.tooltip.tool')}
                    : {data.category}
                  </p>
                  <div className="space-y-0.5">
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.max')}:{' '}
                      {valueFormatter(data.max)}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.q3')} (75%):{' '}
                      {valueFormatter(data.q3)}
                    </p>
                    <p className="font-mono font-medium">
                      {t('analytics.dashboard.complexCharts.tooltip.median')}:{' '}
                      {valueFormatter(data.median)}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.mean')}:{' '}
                      {valueFormatter(data.mean)}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.q1')} (25%):{' '}
                      {valueFormatter(data.q1)}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.min')}:{' '}
                      {valueFormatter(data.min)}
                    </p>
                    <p className="font-mono text-muted-foreground mt-1">
                      {t('analytics.dashboard.complexCharts.tooltip.samples')}: {data.count}
                      {data.outliers.length > 0 &&
                        ` (${data.outliers.length} ${t('analytics.dashboard.complexCharts.tooltip.outliers')})`}
                    </p>
                    {typeof data.toolCount === 'number' && (
                      <p className="font-mono">
                        {t('analytics.dashboard.complexCharts.tooltip.toolCount', {
                          count: data.toolCount,
                        })}
                      </p>
                    )}
                    {data.traceCount && data.traceCount > data.count && (
                      <p className="font-mono text-muted-foreground">
                        {t('analytics.dashboard.complexCharts.tooltip.aggregatedFrom', {
                          count: data.traceCount,
                        })}
                      </p>
                    )}
                  </div>
                </div>
              );
            }}
          />
          {/* Lower whisker (thin line) */}
          <Bar
            dataKey="whiskerLowerHeight"
            stackId="box"
            fill="transparent"
            stroke="var(--border)"
            strokeWidth={2}
            barSize={2}
          />
          {/* Box - lower part */}
          <Bar dataKey="boxLowerHeight" stackId="box" barSize={40}>
            {chartData.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={`${COLORS[index % COLORS.length]}40`}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
              />
            ))}
          </Bar>
          {/* Median line - using a thin bar */}
          <Bar
            dataKey={() => 0}
            stackId="box"
            fill="transparent"
            stroke="transparent"
            barSize={40}
          />
          {/* Box - upper part */}
          <Bar dataKey="boxUpperHeight" stackId="box" barSize={40}>
            {chartData.map((_entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={`${COLORS[index % COLORS.length]}40`}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
              />
            ))}
          </Bar>
          {/* Upper whisker (thin line) */}
          <Bar
            dataKey="whiskerUpperHeight"
            stackId="box"
            fill="transparent"
            stroke="var(--border)"
            strokeWidth={2}
            barSize={2}
          />
          {/* Median marker - using scatter for better visibility */}
          <Scatter
            dataKey="medianPoint"
            fill="transparent"
            shape={(props: unknown) => {
              const { cx, cy } = props as { cx?: number; cy?: number };
              if (cx === undefined || cy === undefined) return <></>;
              return (
                <line x1={cx - 20} y1={cy} x2={cx + 20} y2={cy} stroke="var(--primary)" strokeWidth={3} />
              );
            }}
          />
          {/* Outliers */}
          {showOutliers && outlierData.length > 0 && (
            <Scatter data={outlierData} dataKey="value" fill="var(--destructive)" shape="circle" />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {/* SmartLegend */}
      <SmartLegend payload={legendItems} layout="horizontal" maxVisibleItems={10} />
    </div>
  );
}
